import { useState, useRef, useEffect } from 'react';
import { LogOut, ChevronDown, BarChart3 } from 'lucide-react';

function ProfileDropdown({ user, usage, onSignOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const avatar = user?.user_metadata?.avatar_url;
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || '';
  const email = user?.email || '';

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '4px 10px 4px 4px',
          cursor: 'pointer', transition: 'all 0.15s',
        }}
        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
      >
        {avatar ? (
          <img src={avatar} alt="" style={{ width: 26, height: 26, borderRadius: 6 }} />
        ) : (
          <div style={{
            width: 26, height: 26, borderRadius: 6, background: '#6366f1',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 11, fontWeight: 700,
          }}>
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
        <ChevronDown size={12} color="#71717a" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 240,
          background: '#141419', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          overflow: 'hidden', zIndex: 100,
        }}>
          {/* User info */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ color: '#e4e4e7', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{displayName}</div>
            <div style={{ color: '#52525b', fontSize: 11 }}>{email}</div>
          </div>

          {/* Usage */}
          {usage && !usage.loading && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <BarChart3 size={13} color="#71717a" />
                <span style={{ color: '#a1a1aa', fontSize: 11, fontWeight: 600 }}>Usage this month</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min((usage.used / usage.limit) * 100, 100)}%`, height: '100%',
                    background: usage.atLimit ? '#ef4444' : '#6366f1', borderRadius: 4, transition: 'width 0.3s',
                  }} />
                </div>
                <span style={{ color: usage.atLimit ? '#ef4444' : '#a1a1aa', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                  {usage.used}/{usage.limit}
                </span>
              </div>
            </div>
          )}

          {/* Sign out */}
          <button
            onClick={() => { setOpen(false); onSignOut(); }}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 16px', background: 'none', border: 'none',
              color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', transition: 'background 0.1s',
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(239,68,68,0.06)'}
            onMouseOut={e => e.currentTarget.style.background = 'none'}
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function UnVagueLogo({ size = 32 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: 8,
      overflow: 'hidden', flexShrink: 0,
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      border: '1px solid rgba(255,255,255,0.1)',
    }}>
      <img
        src="/logo-uv.png"
        alt="UV"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
}

export default function Header({ onLogoClick, user, usage, onSignOut }) {
  return (
    <header
      style={{
        height: 64,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 50%, #1d4ed8 100%)',
        borderBottom: '1px solid #1f1f2e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 35,
        flexShrink: 0,
        overflow: 'hidden',
      }}
      data-testid="app-header"
    >
      <div
        className="header-shimmer"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      />

      <div
        onClick={onLogoClick}
        style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', cursor: onLogoClick ? 'pointer' : 'default' }}
      >
        <UnVagueLogo size={24} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: '-0.03em', lineHeight: 1 }}>
            UnVague
          </span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, letterSpacing: '0.02em' }}>
            Requirements Ambiguity Engine
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
        {/* AI Engine badge */}
        <div
          data-testid="ai-engine-badge"
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 20,
            padding: '5px 12px',
          }}
        >
          <span className="pulse-dot" />
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: 500 }}>AI Engine Online</span>
        </div>

        {/* Profile dropdown */}
        {user && <ProfileDropdown user={user} usage={usage} onSignOut={onSignOut} />}
      </div>
    </header>
  );
}
